import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Shield, CheckCircle2, ArrowRight, DollarSign, Building2, Users, Stethoscope, FileText, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const planTypeMeta = [
  { icon: Building2, color: 'sky' },
  { icon: Shield, color: 'slate' }
];
const helpCardIcons = [Users, FileText, DollarSign, Stethoscope];

const getCategoryBgColor = (index) => ['bg-amber-100', 'bg-slate-200', 'bg-yellow-100', 'bg-slate-100'][index] || 'bg-slate-100';
const getCategoryIconColor = (index) => ['text-amber-600', 'text-slate-600', 'text-yellow-600', 'text-slate-500'][index] || 'text-slate-500';

export const HealthPlansPage = () => {
  const { t } = useTranslation();
  const planTypes = t('healthPlans.planTypes', { returnObjects: true });
  const subsidyInfo = t('healthPlans.subsidyInfo', { returnObjects: true });
  const planCategories = t('healthPlans.categories', { returnObjects: true });
  const helpCards = t('healthPlans.helpCards', { returnObjects: true });

  return (
    <div className="overflow-hidden" data-testid="health-plans-page">
      <section className="gradient-hero py-20 lg:py-32" data-testid="hp-hero">
        <div className="container-custom">
          <motion.div initial="initial" animate="animate" variants={fadeInUp} className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-600 rounded-full text-sm font-medium mb-6">
              <Stethoscope className="h-4 w-4" />
              {t('healthPlans.heroBadge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              {t('healthPlans.heroTitle')} <span className="text-gradient">{t('healthPlans.heroHighlight')}</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8">{t('healthPlans.heroDesc')}</p>
            <Link to="/contact">
              <Button className="btn-accent" data-testid="hp-hero-cta">
                {t('healthPlans.heroCta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="hp-comparison">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('healthPlans.comparisonTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('healthPlans.comparisonSubtitle')}</p>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {planTypes.map((plan, index) => {
              const meta = planTypeMeta[index] || planTypeMeta[0];
              const Icon = meta.icon;
              const isSky = meta.color === 'sky';
              return (
                <motion.div key={plan.type} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className={`bg-white rounded-2xl border-2 ${isSky ? 'border-sky-200' : 'border-slate-200'} overflow-hidden`} data-testid={`plan-type-${index}`}>
                  <div className={`p-6 ${isSky ? 'bg-sky-50' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-4">
                      <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${isSky ? 'bg-sky-500' : 'bg-slate-900'}`}>
                        <Icon className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900">{plan.type}</h3>
                        <p className="text-slate-600">{plan.subtitle}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-slate-600 mb-6">{plan.description}</p>
                    <ul className="space-y-3 mb-6">
                      {plan.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-start gap-3">
                          <CheckCircle2 className={`h-5 w-5 flex-shrink-0 mt-0.5 ${isSky ? 'text-sky-500' : 'text-slate-900'}`} />
                          <span className="text-slate-700 text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                    <div className={`p-4 rounded-xl ${isSky ? 'bg-sky-50' : 'bg-slate-50'}`}>
                      <p className={`text-sm font-medium ${isSky ? 'text-sky-700' : 'text-slate-700'}`}>{plan.highlight}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-spacing gradient-subtle" data-testid="hp-subsidies">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-full text-sm font-medium mb-6">
                <DollarSign className="h-4 w-4" />
                {t('healthPlans.subsidyBadge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('healthPlans.subsidyTitle')}</h2>
              <p className="text-lg text-slate-600 mb-6">{t('healthPlans.subsidyText1')}</p>
              <p className="text-slate-600 mb-8">{t('healthPlans.subsidyText2')}</p>
              <Link to="/contact">
                <Button className="btn-primary" data-testid="hp-subsidy-cta">{t('healthPlans.subsidyCta')}</Button>
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-4">
              {subsidyInfo.map((item, index) => (
                <div key={item.range} className="bg-white p-5 rounded-xl border border-slate-100 card-shadow flex items-center justify-between" data-testid={`subsidy-item-${index}`}>
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                    <span className="font-medium text-slate-900">{item.range}</span>
                  </div>
                  <span className="text-green-600 font-medium text-sm">{item.assistance}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="hp-categories">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('healthPlans.categoriesTitle')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('healthPlans.categoriesSubtitle')}</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {planCategories.map((category, index) => (
              <motion.div key={category.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white p-6 rounded-2xl border border-slate-100 card-shadow text-center" data-testid={`category-card-${index}`}>
                <div className={`h-16 w-16 mx-auto rounded-2xl flex items-center justify-center mb-4 ${getCategoryBgColor(index)}`}>
                  <Shield className={`h-8 w-8 ${getCategoryIconColor(index)}`} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{category.name}</h3>
                <p className="text-3xl font-bold text-sky-500 mb-2">{category.coverage}</p>
                <p className="text-slate-500 text-sm mb-4">{t('healthPlans.coverageLevel')}</p>
                <p className="text-slate-600 text-sm">{category.cost}</p>
                <p className="text-slate-500 text-xs mt-2">{category.best}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900" data-testid="hp-help">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="text-center lg:text-left">
              <HelpCircle className="h-12 w-12 text-sky-400 mb-6 mx-auto lg:mx-0" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('healthPlans.helpTitle')}</h2>
              <p className="text-slate-300 text-lg mb-8">{t('healthPlans.helpText')}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/contact">
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-8 rounded-full font-semibold" data-testid="hp-cta-btn">{t('healthPlans.helpCta')}</Button>
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="grid grid-cols-2 gap-4">
              {helpCards.map((card, index) => {
                const Icon = helpCardIcons[index] || Users;
                return (
                  <div key={card.title} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                    <Icon className="h-8 w-8 text-sky-400 mb-4" />
                    <h4 className="text-white font-semibold mb-2">{card.title}</h4>
                    <p className="text-slate-400 text-sm">{card.desc}</p>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthPlansPage;
